// Guestbook — visitors leave a note for a world's creator, the creator replies.
// A reply is a row whose parent_id points at the original note, so a note plus
// its replies is a small thread (the seed of a future DM system).
//
// Row visibility is enforced by RLS (supabase/migrations/20260726_guestbook.sql):
// the world owner sees everything in their world, an author sees their own
// notes and the replies written under them. The queries here can therefore stay
// simple — the database never hands back somebody else's note.
import { supabase } from '../config/supabase';
import { getStoredUserName } from '../utils/nameGenerator';

export interface GuestbookEntry {
  id: string;
  world_id: string;
  author_id: string | null;
  author_name: string;
  message: string;
  parent_id: string | null;
  read_by_owner: boolean;
  created_at: string;
}

/** A top-level note with the replies written under it, oldest reply first. */
export interface GuestbookThread {
  entry: GuestbookEntry;
  replies: GuestbookEntry[];
}

const COLUMNS = 'id,world_id,author_id,author_name,message,parent_id,read_by_owner,created_at';

// Group a flat row set into threads: top-level notes newest first, replies
// nested under their parent oldest first (reads like a conversation).
function toThreads(rows: GuestbookEntry[]): GuestbookThread[] {
  const tops = rows.filter((r) => !r.parent_id);
  const repliesByParent = new Map<string, GuestbookEntry[]>();
  for (const r of rows) {
    if (!r.parent_id) continue;
    const list = repliesByParent.get(r.parent_id);
    if (list) list.push(r);
    else repliesByParent.set(r.parent_id, [r]);
  }
  const byOldest = (a: GuestbookEntry, b: GuestbookEntry) => a.created_at.localeCompare(b.created_at);
  return tops
    .sort((a, b) => b.created_at.localeCompare(a.created_at))
    .map((entry) => ({ entry, replies: (repliesByParent.get(entry.id) || []).sort(byOldest) }));
}

async function fetchRows(worldId: string): Promise<GuestbookEntry[]> {
  const { data, error } = await supabase
    .from('guestbook_entries')
    .select(COLUMNS)
    .eq('world_id', worldId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data as GuestbookEntry[]) || [];
}

/** Owner view: every note left in this world, newest first, replies nested. */
export async function fetchEntriesForWorld(worldId: string): Promise<GuestbookThread[]> {
  return toThreads(await fetchRows(worldId));
}

/** Visitor view: only the notes I wrote here, with the owner's replies nested. */
export async function fetchMyEntries(worldId: string): Promise<GuestbookThread[]> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return [];
  const rows = await fetchRows(worldId);
  const mine = new Set(rows.filter((r) => !r.parent_id && r.author_id === user.id).map((r) => r.id));
  return toThreads(rows.filter((r) => (r.parent_id ? mine.has(r.parent_id) : mine.has(r.id))));
}

// Display name for a new entry: the account's profile username when we have
// one, otherwise the same stored/generated name the viewer already shows above
// this visitor's avatar (see getStoredUserName).
async function resolveAuthorName(userId: string): Promise<string> {
  try {
    const { data } = await supabase.from('profiles').select('username').eq('id', userId).single();
    const username = (data as { username?: string } | null)?.username;
    if (username) return username;
  } catch {
    /* profile lookup is best-effort */
  }
  try {
    return getStoredUserName();
  } catch {
    return 'Guest';
  }
}

/**
 * Sign the guestbook (or reply to a note when parentId is given).
 * A new TOP-LEVEL note also pings the creator by email — fire-and-forget, the
 * UI never waits on it and a mail failure never fails the note.
 */
export async function addEntry(worldId: string, message: string, parentId?: string): Promise<GuestbookEntry> {
  const text = message.trim();
  if (!text) throw new Error('Write something first');
  if (text.length > 1000) throw new Error('Notes are limited to 1000 characters');

  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Sign in to leave a note');

  const { data, error } = await supabase
    .from('guestbook_entries')
    .insert({
      world_id: worldId,
      author_id: user.id,
      author_name: await resolveAuthorName(user.id),
      message: text,
      parent_id: parentId ?? null,
    })
    .select(COLUMNS)
    .single();
  if (error) throw error;

  const entry = data as GuestbookEntry;
  if (!entry.parent_id) notifyOwner(entry.id);
  return entry;
}

// Ask the Pages Function to email the world owner. Deliberately unawaited and
// silent: notification is a nicety, the note is already saved.
function notifyOwner(entryId: string): void {
  try {
    fetch('/api/guestbook-notify', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ entryId }),
    }).catch(() => { /* ignore */ });
  } catch {
    /* ignore */
  }
}

/** Owner action: mark a note as read (no-op for anyone else — RLS blocks it). */
export async function markRead(entryId: string): Promise<void> {
  const { error } = await supabase
    .from('guestbook_entries')
    .update({ read_by_owner: true })
    .eq('id', entryId);
  if (error) throw error;
}

/** How many top-level notes in this world the owner hasn't read yet. */
export async function unreadCount(worldId: string): Promise<number> {
  const { count, error } = await supabase
    .from('guestbook_entries')
    .select('id', { count: 'exact', head: true })
    .eq('world_id', worldId)
    .is('parent_id', null)
    .eq('read_by_owner', false);
  if (error) throw error;
  return count || 0;
}

/** Remove a note (author or world owner). */
export async function deleteEntry(entryId: string): Promise<void> {
  const { error } = await supabase.from('guestbook_entries').delete().eq('id', entryId);
  if (error) throw error;
}
